import { useEffect, useRef, useState } from 'react';
import { listenEmbedMessages, mintEmbedToken, postToEmbed } from './embedBridge';
import { useMapperPluginConfig } from './mapperConfig';

/**
 * Host page for the hosted Mapper embed handshake.
 * Listens for mapper:ready from the iframe, mints a scoped token for an allowlisted
 * parent origin, then passes token + config via origin-validated postMessage.
 */
export default function MapperEmbedHost() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { data: configData, isLoading, error } = useMapperPluginConfig();
  const [handshakeError, setHandshakeError] = useState<string | null>(null);
  const [handshakeOk, setHandshakeOk] = useState(false);
  const embedOrigin = window.location.origin;
  const iframeSrc = `${embedOrigin}/mapper/embed`;

  useEffect(() => {
    const allowlist = configData?.config?.embed?.allowed_origins?.filter(Boolean) || [];
    if (allowlist.length === 0) return;

    return listenEmbedMessages(allowlist, async (msg, origin) => {
      if (msg.type !== 'mapper:ready') return;
      setHandshakeError(null);
      try {
        const { token, config } = await mintEmbedToken(origin, 'sandbox');
        postToEmbed(
          iframeRef.current,
          { type: 'mapper:config', token, config: config as Record<string, unknown> },
          embedOrigin,
        );
        setHandshakeOk(true);
      } catch (err) {
        setHandshakeError(err instanceof Error ? err.message : 'Embed token mint failed');
      }
    });
  }, [configData?.config?.embed?.allowed_origins, embedOrigin]);

  const allowlist = configData?.config?.embed?.allowed_origins?.filter(Boolean) || [];

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-4 py-2 text-xs text-slate-600">
        Mapper embed host — handshake via postMessage (no session JWT in iframe)
        {handshakeOk && <span className="ml-2 text-emerald-600">Token delivered</span>}
        {handshakeError && <span className="ml-2 text-red-600">{handshakeError}</span>}
        {!allowlist.length && !isLoading && (
          <span className="ml-2 text-amber-600">Configure settings.mapper.embed.allowed_origins first</span>
        )}
        {error && <span className="ml-2 text-red-600">Config load failed</span>}
      </div>
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Mapper embed"
        className="min-h-0 flex-1 border-0"
        allow="geolocation"
      />
    </div>
  );
}
